import React, { useEffect, useRef } from 'react';

// The PureJS asset selector is loaded from Adobe's CDN — there's no npm
// equivalent. It exposes `window.PureJSSelectors.renderAssetSelector(mount, opts)`
// which paints the selector into a host-provided element.
const ASSET_SELECTOR_SRC =
  'https://experience.adobe.com/solutions/CQ-assets-selectors/static-assets/resources/assets-selectors.js';

function loadAssetSelectorScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('no window'));
      return;
    }
    if (window.PureJSSelectors) {
      resolve(window.PureJSSelectors);
      return;
    }
    const existing = document.querySelector(`script[src="${ASSET_SELECTOR_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.PureJSSelectors));
      existing.addEventListener('error', reject);
      if (window.PureJSSelectors) resolve(window.PureJSSelectors);
      return;
    }
    const script = document.createElement('script');
    script.src = ASSET_SELECTOR_SRC;
    script.onload = () => resolve(window.PureJSSelectors);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function isDirectoryAsset(asset) {
  if (!asset) return true;
  if (asset['repo:assetClass'] === 'directory') return true;
  if (asset['dc:format'] === 'application/vnd.adobecloud.directory+json') return true;
  return false;
}

export default function ImagesTab({
  imsToken,
  imsOrg,
  repositoryId,
  damFolderPath,
  refreshKey,
  selection,
  uploadStatus,
  onDismissUploadStatus,
  onAssetPick,
}) {
  const containerRef = useRef(null);
  // Latest onAssetPick is held in a ref so the selector callbacks see fresh
  // closures without our having to remount the selector on every parent render.
  const onAssetPickRef = useRef(onAssetPick);
  useEffect(() => { onAssetPickRef.current = onAssetPick; }, [onAssetPick]);

  // Stable mount key. We only re-render the selector when this *meaningfully*
  // changes (new context values + new refresh key). useUEContext's 1.5s poll
  // can briefly wipe its state during postMessage round-trips, so we skip the
  // mount entirely when any required value is empty rather than tearing down
  // and rebuilding the selector — that tear-down was the visible blip.
  const lastMountKeyRef = useRef('');

  const ready = !!(imsToken && imsOrg && repositoryId && damFolderPath);
  const hasSelection = !!selection?.cleanResource;
  const showUploadBanner = uploadStatus && (uploadStatus.inProgress || uploadStatus.failed > 0);

  useEffect(() => {
    if (!imsToken || !imsOrg || !repositoryId || !damFolderPath) return;

    const mountKey = `${refreshKey}|${imsToken}|${imsOrg}|${repositoryId}|${damFolderPath}`;
    if (lastMountKeyRef.current === mountKey) return;

    const mount = containerRef.current;
    if (!mount) return;

    lastMountKeyRef.current = mountKey;
    let cancelled = false;

    (async () => {
      try {
        await loadAssetSelectorScript();
        if (cancelled || !mount) return;
        const PJS = window.PureJSSelectors;
        if (!PJS || typeof PJS.renderAssetSelector !== 'function') {
          console.warn('[DemoPilot] PureJSSelectors not available after script load');
          return;
        }
        // Clear right before re-render so the swap is atomic. Done inside the
        // async block (not in cleanup) so transient dep changes don't flash.
        mount.innerHTML = '';
        PJS.renderAssetSelector(mount, {
          imsToken,
          imsOrg,
          repositoryId,
          path: damFolderPath,
          rail: true,
          noWrap: true,
          colorScheme: 'light',
          hideTreeNav: true,
          hideFiltersButton: true,
          featureSet: ['upload'],
          acvConfig: { selectionType: 'single' },
          handleAssetSelection: (assets) => {
            const asset = assets && assets[0];
            if (isDirectoryAsset(asset)) return;
            const damPath = asset['repo:path'] || asset.path;
            if (damPath && typeof onAssetPickRef.current === 'function') {
              onAssetPickRef.current(damPath, asset);
            }
          },
          handleNavigateToAsset: (asset) => {
            if (isDirectoryAsset(asset)) return;
            const damPath = asset['repo:path'] || asset.path;
            if (damPath && typeof onAssetPickRef.current === 'function') {
              onAssetPickRef.current(damPath, asset);
            }
          },
        });
      } catch (err) {
        console.error('[DemoPilot] Asset Selector load/render failed:', err);
      }
    })();

    // Only cancel the in-flight script load on cleanup — leave the rendered
    // selector DOM in place so transient dep flips don't visibly tear it down.
    return () => { cancelled = true; };
  }, [refreshKey, imsToken, imsOrg, repositoryId, damFolderPath]);

  return (
    <div className="dp-tab-content dp-tab-content--fill">
      {showUploadBanner && (
        <div key="upload-status" className={`dp-upload-banner ${uploadStatus.inProgress ? '' : uploadStatus.failed ? 'is-error' : 'is-success'}`}>
          {uploadStatus.inProgress && <span className="dp-spinner" aria-hidden />}
          <span className="dp-upload-banner-text">
            {uploadStatus.inProgress
              ? `Uploading ${uploadStatus.done + uploadStatus.failed}/${uploadStatus.total} to AEM…`
              : `${uploadStatus.done} uploaded, ${uploadStatus.failed} failed`}
          </span>
          {!uploadStatus.inProgress && (
            <button
              className="dp-upload-banner-dismiss"
              onClick={onDismissUploadStatus}
              type="button"
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div key="hint" className="dp-asset-selector-hint">
        <span className="dp-hint-icon" aria-hidden>ⓘ</span>
        <span>
          {hasSelection
            ? 'Pick an asset to replace the selected image.'
            : 'Select an image on the page first.'}
        </span>
      </div>

      {!ready && (
        <div key="connecting" className="dp-status">
          <span className="dp-spinner" aria-hidden />
          <span>Connecting to AEM…</span>
        </div>
      )}

      <div key="selector-mount" ref={containerRef} className="dp-asset-selector-mount" />
    </div>
  );
}
